import { Component, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { map, startWith } from 'rxjs';
import { LanguageService } from '../../../../../core/services/language.service';
import { DropdownComponent } from '../../../../../osp-ui/components/form/dropdown/dropdown.component';
import { ErrorComponent } from '../../../../../osp-ui/components/form/form-field/components/error/error.component';
import { LabelComponent } from '../../../../../osp-ui/components/form/form-field/components/label/label.component';
import { FormFieldComponent } from '../../../../../osp-ui/components/form/form-field/form-field.component';
import { InputNumberComponent } from '../../../../../osp-ui/components/form/input-number/input-number.component';
import { InputTextComponent } from '../../../../../osp-ui/components/form/input-text/input-text.component';
import { SelectComponent } from '../../../../../osp-ui/components/form/select/select.component';
import { IntervalsDuration } from '../../../../../shared/enums/intervals-duration.enum';
import { IntervalDurationResolverPipe } from '../../../../../shared/pipes/interval-duration-resolver.pipe';
import { TelematicsUnitType } from '../../../../resources/equipment/enums/telematics-unit-type.enum';
import {
  EquipmentTelematicsUnitResolver
} from '../../../../resources/equipment/resolver/equipment-telematics-unit-resolver';
import { TELEMATICS_UNITS_TYPES_WITHOUT_BEACONS } from '../../../constants/telematics-unit-types-without-beacons.const';
import { AlarmSettingsFieldData } from '../../../contract/alarm-settings-field-data.contract';
import { AlarmRuleControl } from '../../../enums/alarm-rule-control.enum';
import { TelematicsSelector } from '../../../enums/telematics-selector.enum';
import { AlarmSettingsBase } from '../alarm-settings-base.class';
import { AlarmSettingsFormControls } from '../contract/alarm-settings-form-controls.contract';

@Component({
  selector: 'osp-inactive-telematic-unit-settings',
  templateUrl: './inactive-telematic-unit-settings.component.html',
  styleUrls: ['./inactive-telematic-unit-settings.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputNumberComponent,
    DropdownComponent,
    SelectComponent,
    TranslateModule,
    InputTextComponent,
    FormFieldComponent,
    LabelComponent,
    ErrorComponent,
  ],
})
export class InactiveTelematicUnitSettingsComponent extends AlarmSettingsBase implements OnInit {
  private destroyRef = inject(DestroyRef);

  protected readonly REGEX_POSITIVE_INTEGER_NOT_STARTING_WITH_ZERO = new RegExp('^[1-9][0-9]*$');
  protected readonly boundIntervalDurationResolver = this.intervalDurationResolver.transform.bind(
    this.intervalDurationResolver,
  );
  protected readonly boundResolveTelematicsSelectorName = this.resolveTelematicsSelectorName.bind(this);
  protected readonly intervalDurations = this.resolveIntervalDurationOptions([
    IntervalsDuration.HOURS,
    IntervalsDuration.DAYS,
    IntervalsDuration.MONTHS,
  ]);
  protected readonly telematicsSelectors = this.resolveTelematicsSelectorOptions();
  protected readonly telematicsUnitTypes = this.resolveTelematicsUnitsOptions([
    TelematicsUnitType.TRACKUNIT_1,
    TelematicsUnitType.TELTONIKA_OBD_UNIT,
    TelematicsUnitType.TELTONIKA_CABLE_UNIT,
    TelematicsUnitType.CONFIDEX_ROUGH,
    TelematicsUnitType.CONFIDEX_MICRO,
    TelematicsUnitType.SIGFOX_TRACKERTYPL26_1,
    TelematicsUnitType.AEMP,
    TelematicsUnitType.GENERIC_2,
    TelematicsUnitType.GENERIC_3,
    TelematicsUnitType.GENERIC_4,
    TelematicsUnitType.GENERIC_5,
    TelematicsUnitType.GPS_OVER_IP,
    TelematicsUnitType.RIO,
    TelematicsUnitType.DIGITAL_MATTER_OYSTER3_UNIT,
    TelematicsUnitType.DIGITAL_MATTER_BARRA_UNIT,
  ]);

  protected settingsLimitLabel = '';
  protected settingsLimitUnitLabel = '';
  protected settingsTelematicsSelectorLabel = '';
  protected settingsTelematicsTypesLabel = '';
  protected settingsLimitExplanation: string = this.translate(
    'modules.notification.alarmRuleAddEdit.expl.inactiveTelematicsUnit',
  );
  protected allowSelectTelematicUnits = signal<boolean>(false);

  @Input() set form(form: FormGroup<AlarmSettingsFormControls>) {
    this.settingsForm = form;
  }

  @Input() set fieldData(fieldData: AlarmSettingsFieldData) {
    this.alarmSettingsFieldData = fieldData;
  }

  constructor(
    private telematicUnitResolver: EquipmentTelematicsUnitResolver,
    private languageService: LanguageService,
    private intervalDurationResolver: IntervalDurationResolverPipe,
  ) {
    super();
  }

  public ngOnInit(): void {
    if (this.settingsForm) {
      this.initListenerTelematicsSelection();
      this.onTelematicsSelectorChanged();
    }

    this.setLabels();
  }

  private initListenerTelematicsSelection(): void {
    const control = this.settingsForm.get(this.controlNames.SETTINGS_TELEMATICS_SELECTOR);
    if (control) {
      control.valueChanges
        .pipe(
          startWith(control.value),
          map((value) => value === TelematicsSelector.SELECTED),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((value) => {
          this.allowSelectTelematicUnits.set(value);
          this.onTelematicsSelectorChanged();
        });
    }
  }

  private onTelematicsSelectorChanged(): void {
    const telematicsTypesControl = this.settingsForm.get<AlarmRuleControl>(this.controlNames.SETTINGS_TELEMATICS_TYPES);

    if (this.allowSelectTelematicUnits()) {
      telematicsTypesControl?.setValidators([Validators.required]);
    } else if (
      this.settingsForm.get(this.controlNames.SETTINGS_TELEMATICS_SELECTOR)?.value ===
      TelematicsSelector.ALL_TELEMATIC_UNITS_EXCEPT_BEACONS
    ) {
      telematicsTypesControl?.setValue(Array.from(TELEMATICS_UNITS_TYPES_WITHOUT_BEACONS));
      telematicsTypesControl?.setValidators(null);
    } else {
      telematicsTypesControl?.setValue([]);
      telematicsTypesControl?.setValidators(null);
    }

    telematicsTypesControl?.updateValueAndValidity();
  }

  private resolveTelematicsUnitName(option: string): string {
    const title = this.telematicUnitResolver.resolveName(option);
    const subtitle = this.telematicUnitResolver.resolveSubtitle(option);
    return !subtitle ? title : `${title} - ${subtitle}`;
  }

  private resolveTelematicsUnitsOptions(unitTypes: TelematicsUnitType[]) {
    return unitTypes.map((unitType) => ({ value: unitType, label: this.resolveTelematicsUnitName(unitType) }));
  }

  private resolveTelematicsSelectorName(selector: string): string {
    switch (selector) {
      case TelematicsSelector.ALL:
        return this.languageService.getInstant('general.all');
      case TelematicsSelector.SELECTED:
        return this.languageService.getInstant('general.selected');
      case TelematicsSelector.ALL_TELEMATIC_UNITS_EXCEPT_BEACONS:
        return this.languageService.getInstant(
          'modules.notification.alarmRuleAddEdit.inactiveTelematicsUnit.allTelematicUnitsExceptBeacons',
        );
      default:
        return 'Unknown telematics selector name';
    }
  }

  private resolveTelematicsSelectorOptions() {
    return Object.keys(TelematicsSelector).map((selector) => ({
      value: selector as TelematicsSelector,
      label: this.boundResolveTelematicsSelectorName(selector),
    }));
  }

  private resolveIntervalDurationOptions(intervalDurations: IntervalsDuration[]) {
    return intervalDurations.map((duration) => ({
      value: duration,
      label: this.boundIntervalDurationResolver(duration),
    }));
  }

  private setLabels(): void {
    this.settingsLimitLabel = this.getFieldLabel(this.controlNames.SETTINGS_LIMIT);
    this.settingsLimitUnitLabel = this.getFieldLabel(this.controlNames.SETTINGS_LIMIT_UNIT);
    this.settingsTelematicsSelectorLabel = this.getFieldLabel(this.controlNames.SETTINGS_TELEMATICS_SELECTOR);
    this.settingsTelematicsTypesLabel = this.getFieldLabel(this.controlNames.SETTINGS_TELEMATICS_TYPES);
  }

  private translate(key: string): string {
    return this.languageService.getInstant(key);
  }
}
